import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { map, Observable, Subscription, switchMap, take } from 'rxjs';
import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles/enums';
import { Store } from '@ngrx/store';
import { filter } from 'rxjs/operators';

import { RootPaths } from '@fcom/core/constants';
import { CmsPageService, MediaQueryService } from '@fcom/common/services';
import { getBackgroundImageForCmsTemplate } from '@fcom/cms/services/cms-utils/cms-utils';
import { CmsImageData } from '@fcom/core-api/interfaces';
import { ButtonTheme } from '@fcom/ui-components/components/buttons';
import { IconPosition } from '@fcom/ui-components/components/icons';
import { CasCustomerType, CasLoginStatus, LoginStep } from '@fcom/core-api/login';
import { casProfileOrNot, loginState } from '@fcom/core/selectors';
import { finShare } from '@fcom/rx/operators/finShare';
import { AppState } from '@fcom/core/interfaces';
import { LoginActions } from '@fcom/core/actions';
import { unsubscribe } from '@fcom/core/utils';

@Component({
  selector: 'fin-corporate-hero',
  templateUrl: './corporate-hero.component.html',
  styleUrl: './corporate-hero.component.scss',
})
export class CorporateHeroComponent implements OnInit, OnDestroy {
  readonly ButtonTheme = ButtonTheme;
  readonly SvgLibraryIcon = SvgLibraryIcon;
  readonly IconPosition = IconPosition;

  @Input({ required: true }) title: string;
  @Input({ required: true }) description: string;
  @Input() showBookingWidget: boolean;

  backgroundImage$: Observable<CmsImageData>;
  showLoginButton$: Observable<boolean>;
  isMobile$: Observable<boolean>;
  isCorporate$: Observable<boolean>;

  subscriptions = new Subscription();

  constructor(
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private pageService: CmsPageService,
    private mediaQueryService: MediaQueryService,
    private store$: Store<AppState>
  ) {}

  ngOnInit(): void {
    const [urlSegment] = this.activatedRoute.snapshot.url.slice(-1);
    const cmsPath = `${RootPaths.CORPORATE}/${urlSegment.path}`;
    const template$ = this.pageService.getPageData(cmsPath);
    this.backgroundImage$ = getBackgroundImageForCmsTemplate(template$);
    this.isMobile$ = this.mediaQueryService.isBreakpoint$('tablet_down');
    const profile$ = this.store$.pipe(casProfileOrNot(), finShare());
    this.isCorporate$ = profile$.pipe(map((profile) => profile?.customerType === CasCustomerType.CORPORATE_USER));
    this.showLoginButton$ = this.store$.pipe(
      loginState(),
      map(({ casLoginStatus }) => casLoginStatus === CasLoginStatus.NOT_LOGGED_IN),
      finShare()
    );
    this.subscriptions.add(
      this.activatedRoute.queryParams
        .pipe(
          filter((params) => params['openLogin']),
          switchMap(() => this.showLoginButton$),
          filter(Boolean),
          take(1)
        )
        .subscribe(() => {
          this.removeQueryParams();
          this.openLogin();
        })
    );
  }

  removeQueryParams(): void {
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: {},
    });
  }

  openLogin(): void {
    this.store$.dispatch(LoginActions.setLoginPhaseStep({ step: LoginStep.CORPORATE_CREDENTIALS_DIRECT }));
  }

  ngOnDestroy(): void {
    unsubscribe(this.subscriptions);
  }
}
